import { useCallback, useEffect, useState } from 'react';

import i18n, { i18nNS } from '../../../../../../i18n';
import { unix } from '../../../../../../utils/datetime';
import { useInterval } from '../../../../../../utils/timer';
import { useClassContext } from '../../../../../class/Context';
import { usePollContext } from '../../../../Context';
import { getIsPollPublished } from '../../../helpers';

export interface UsePublishHelper {
  triggerValidations: () => Promise<boolean>;
}

const usePublishHelper = ({ triggerValidations }: UsePublishHelper) => {
  const { cls } = useClassContext();

  const { poll } = usePollContext();

  const isPollPublished = getIsPollPublished(poll);

  const [isPollInvalid, setIsPollInvalid] = useState(false);
  const [color, setColor] = useState<'primary' | 'error' | 'success'>('error');
  const [message, setMessage] = useState('');

  const { start: startTimer, stop: stopTimer } = useInterval(() => {
    if (!cls?.id) return;

    const isClassOver = cls.endTime < unix();
    if (isClassOver) {
      stopTimer();
      setColor('error');
      setMessage(i18n.t('poll_cannot_be_published_because_the_lecture_is_over', { ns: i18nNS.POLL }));
      return;
    }

    const isClassInFuture = cls.startTime > unix();
    if (isClassInFuture) {
      setColor('primary');
      setMessage(i18n.t('poll_can_be_published_during_the_lecture', { ns: i18nNS.POLL }));
      return;
    }

    setColor('success');
    setMessage(i18n.t('poll_can_be_successfully_published', { ns: i18nNS.POLL }));
  }, null);

  const initialize = useCallback(async () => {
    if (isPollPublished) return;

    const isValid = await triggerValidations();
    if (!isValid) {
      setIsPollInvalid(true);
      setColor('error');
      // setMessage(i18n.t("poll_has_errors_and_cannot_be_published", { ns: i18nNS.POLL }));
      return;
    }

    setIsPollInvalid(false);
    startTimer(1000);
  }, [isPollPublished, triggerValidations, startTimer]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const [isViewErrorsDialogOpen, setIsViewErrorsDialogOpen] = useState(false);

  const handleCloseViewErrorsDialog = () => {
    setIsViewErrorsDialogOpen(false);
  };

  const handleClickViewErrors = () => {
    setIsViewErrorsDialogOpen(true);
  };

  return {
    isPollInvalid,
    color,
    message,
    isViewErrorsDialogOpen,
    handleCloseViewErrorsDialog,
    handleClickViewErrors,
  };
};

export default usePublishHelper;
